import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { friendlyError } from "@/lib/api";
import { ageRangeOptions, reporterOptions } from "@/lib/labels";
import { casesApi } from "@/lib/endpoints";
import { validateReport } from "@/lib/validation";
import { LocationSelect } from "@/components/LocationSelect";
import { resolveGeocode } from "@/lib/venezuelaLocations";
import { PhoneVerify } from "@/components/PhoneVerify";
import { mapsAvailable, MapPicker } from "@/components/GoogleMaps";

export default function ReportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", age_range: "", name_unknown: false,
    eye_color: "", skin_color: "", distinguishing_marks: "", clothing: "",
    shelter_name: "", found_location: "",
    state: "", municipality: "", lat: "", lng: "", reporter_type: "", reporter_phone: "",
  });
  const [geoLoading, setGeoLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPin = ({ lat, lng }) => setForm((f) => ({ ...f, lat: String(lat), lng: String(lng) }));
  const [idToken, setIdToken] = useState("");

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const detectLocation = () => {
    if (!navigator.geolocation) { toast.error(t("report.toastGeoUnavailable")); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude, longitude } = pos.coords;
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=12&accept-language=es`);
        const data = await res.json();
        const a = data.address || {};
        const { state: st, municipality: mu } = resolveGeocode(a.state, a.county || a.city || a.town || a.municipality || a.suburb || "");
        if (st) {
          setForm((f) => ({ ...f, state: st, municipality: mu }));
          toast.success(mu ? t("report.toastLocDetected") : t("report.toastStateDetected"));
        } else {
          toast.message(t("report.toastNoState"));
        }
      } catch {
        toast.error(t("report.toastLocError"));
      } finally {
        setGeoLoading(false);
      }
    }, () => {
      toast.error(t("report.toastGeoDenied"));
      setGeoLoading(false);
    }, { timeout: 10000 });
  };

  const submit = async (e) => {
    e.preventDefault();
    const error = validateReport(form, !!photo, t);
    if (error) { toast.error(error); return; }
    if (!consent) { toast.error(t("report.toastConsent")); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("photo", photo);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("firebase_id_token", idToken);
      await casesApi.create(fd);
      setDone(true);
      toast.success(t("report.toastPublished"));
    } catch (err) {
      toast.error(friendlyError(err, t("report.toastSendError")));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center text-center gap-4 py-10" data-testid="report-success">
        <CheckCircle2 className="w-14 h-14 text-[#166534]" />
        <h1 className="font-heading text-2xl font-bold">{t("report.successTitle")}</h1>
        <p className="text-neutral-600 max-w-sm">
          {t("report.successText")}
        </p>
        <div className="flex flex-col gap-2 w-full max-w-xs">
          <button data-testid="go-search-btn" onClick={() => navigate("/")}
            className="bg-[#0066CC] text-white hover:bg-[#0052A3] rounded-sm min-h-[48px] font-semibold">
            {t("report.goSearch")}
          </button>
          <button data-testid="report-another-btn" onClick={() => window.location.reload()}
            className="bg-white border border-neutral-300 hover:bg-neutral-50 rounded-sm min-h-[48px] font-semibold">
            {t("report.reportAnother")}
          </button>
        </div>
      </div>
    );
  }

  const labelCls = "text-sm font-medium text-neutral-900 mb-1.5 block";
  const inputCls = "w-full min-h-[48px] rounded-sm border border-neutral-300 bg-white px-3 text-base focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC] outline-none";

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" data-testid="report-form">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{t("report.title")}</h1>
        <p className="text-sm text-neutral-600 mt-1">{t("report.subtitle")}</p>
      </div>

      <div>
        <label className={labelCls}>{t("report.photoLabel")}</label>
        <input ref={fileRef} type="file" accept="image/*" capture="environment"
          onChange={onPhoto} className="hidden" data-testid="photo-input" />
        <div onClick={() => fileRef.current?.click()} data-testid="photo-dropzone"
          className="bg-neutral-50 border-2 border-dashed border-neutral-300 p-4 text-center cursor-pointer min-h-[120px] flex flex-col items-center justify-center gap-2 rounded-sm">
          {preview ? (
            <img src={preview} alt="preview" className="max-h-48 rounded-sm object-contain" />
          ) : (
            <>
              <Camera className="w-8 h-8 text-neutral-400" />
              <span className="text-sm text-neutral-600">{t("report.photoHint")}</span>
            </>
          )}
        </div>
      </div>

      <div>
        <label className={labelCls}>{t("report.ageLabel")}</label>
        <select data-testid="input-age-range" className="native-select" value={form.age_range} onChange={set("age_range")}>
          <option value="">{t("common.select")}</option>
          {ageRangeOptions(t).map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>

      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input type="checkbox" className="w-5 h-5 accent-[#0066CC]" checked={form.name_unknown}
          onChange={(e) => setForm((f) => ({ ...f, name_unknown: e.target.checked }))} data-testid="input-name-unknown" />
        <span className="text-sm font-medium text-neutral-900">{t("report.nameUnknownLabel")}</span>
      </label>

      {form.name_unknown ? (
        <div className="flex flex-col gap-3 border border-neutral-200 rounded-sm p-3 bg-neutral-50" data-testid="characteristics-block">
          <p className="text-xs text-neutral-500">{t("report.describeHint")}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t("report.eyeColor")}</label>
              <input data-testid="input-eye-color" className={inputCls} value={form.eye_color} onChange={set("eye_color")} />
            </div>
            <div>
              <label className={labelCls}>{t("report.skinColor")}</label>
              <input data-testid="input-skin-color" className={inputCls} value={form.skin_color} onChange={set("skin_color")} />
            </div>
          </div>
          <div>
            <label className={labelCls}>{t("report.marks")}</label>
            <input data-testid="input-marks" className={inputCls} value={form.distinguishing_marks} onChange={set("distinguishing_marks")}
              placeholder={t("report.marksPlaceholder")} />
          </div>
          <div>
            <label className={labelCls}>{t("report.clothing")}</label>
            <input data-testid="input-clothing" className={inputCls} value={form.clothing} onChange={set("clothing")}
              placeholder={t("report.clothingPlaceholder")} />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>{t("report.firstName")}</label>
            <input data-testid="input-first-name" className={inputCls} value={form.first_name} onChange={set("first_name")} />
          </div>
          <div>
            <label className={labelCls}>{t("report.lastName")}</label>
            <input data-testid="input-last-name" className={inputCls} value={form.last_name} onChange={set("last_name")} />
          </div>
        </div>
      )}

      <div>
        <label className={labelCls}>{t("report.shelter")}</label>
        <input data-testid="input-shelter" className={inputCls} value={form.shelter_name} onChange={set("shelter_name")}
          placeholder={t("report.shelterPlaceholder")} />
        <p className="text-xs text-neutral-500 mt-1">{t("report.shelterHint")}</p>
      </div>

      <div>
        <label className={labelCls}>{t("report.foundLocation")}</label>
        <input data-testid="input-found-location" className={inputCls} value={form.found_location} onChange={set("found_location")}
          placeholder={t("report.foundLocationPlaceholder")} />
        <p className="text-xs text-neutral-500 mt-1">{t("report.foundLocationHint")}</p>
        {mapsAvailable() && (
          <div className="mt-2">
            <p className="text-xs text-neutral-500 mb-1">{t("report.mapHint")}</p>
            <MapPicker lat={form.lat ? Number(form.lat) : null} lng={form.lng ? Number(form.lng) : null} onChange={setPin} />
            {form.lat && form.lng && <p className="text-xs text-[#166534] mt-1">{t("report.pinMarked")}</p>}
          </div>
        )}
      </div>

      <div>
        <label className={labelCls}>{t("report.stateMuni")}</label>
        <LocationSelect state={form.state} municipality={form.municipality}
          onChange={({ state, municipality }) => setForm((f) => ({ ...f, state, municipality }))}
          onDetect={detectLocation} detecting={geoLoading} prefix="report-loc" />
      </div>

      <div>
        <label className={labelCls}>{t("report.whoReports")}</label>
        <select data-testid="input-reporter-type" className="native-select" value={form.reporter_type} onChange={set("reporter_type")}>
          {reporterOptions(t).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div>
        <label className={labelCls}>{t("report.contactPhone")}</label>
        <PhoneVerify testid="report-verify" phone={form.reporter_phone}
          onPhoneChange={(v) => setForm((f) => ({ ...f, reporter_phone: v }))}
          verified={!!idToken} onVerified={setIdToken} onReset={() => setIdToken("")} />
        <p className="text-xs text-neutral-500 mt-1">{t("report.contactPhoneHint")}</p>
      </div>

      <label className="flex items-start gap-2 cursor-pointer select-none border border-neutral-200 rounded-sm p-3 bg-neutral-50" data-testid="consent-label">
        <input type="checkbox" className="w-5 h-5 mt-0.5 shrink-0 accent-[#0066CC]" checked={consent}
          onChange={(e) => setConsent(e.target.checked)} data-testid="consent-checkbox" />
        <span className="text-sm text-neutral-700">
          {t("report.consent")}
        </span>
      </label>

      {!idToken && <p className="text-xs text-[#B45309] -mt-1">{t("report.mustVerify")}</p>}
      <button type="submit" disabled={submitting || !consent || !idToken} data-testid="submit-report-btn"
        className="bg-[#0066CC] text-white hover:bg-[#0052A3] disabled:opacity-60 disabled:cursor-not-allowed rounded-sm min-h-[48px] font-semibold flex items-center justify-center gap-2">
        {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> {t("report.submitting")}</> : t("report.submit")}
      </button>
    </form>
  );
}
